import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MapView, type LocationMark } from './MapView'
import { SidePanel, SIDE_DEFAULT, SIDE_MAX, SIDE_MIN } from './SidePanel'
import { SlotPanel } from './SlotPanel'
import { TaskList } from './TaskList'
import { TaskPrepPanel } from './TaskPrep'
import { useRealtime } from './useRealtime'
import { useTasks } from './useTasks'
import {
  ERASER_SIZES,
  PEN_WIDTHS,
  PIN_COLORS,
  type Board,
  type EraserMode,
  type GamePos,
  type Pin,
  type StrokePart,
  type Tool,
} from './types'
import {
  TARKOV_MAPS,
  TARKOV_MAP_LIST,
  defaultVisibleLayers,
  visibleOnLayers,
  type TarkovMapDef,
} from './tarkovMaps'
import { objectiveText } from './tarkovData'
import { canonicalizeBoard } from './taskVariants'
import { ShareBar } from './ShareBar'
import { AboutDialog } from './About'
import { LangToggle, useI18n, type MsgKey } from './i18n'

const LS_USER = 'tm.user'
const LS_MAP = 'tm.mapId'
const LS_COLOR = 'tm.color'
const LS_TAB = 'tm.tab'
const LS_SIDE = 'tm.side'
const LS_SIDE_W = 'tm.sideWidth'
const LS_LOCS = 'tm.locs'
const LS_TOOL = 'tm.tool'
const LS_PEN_COLOR = 'tm.penColor'
const LS_PEN_W = 'tm.penWidth'
const LS_ERASER_SIZE = 'tm.eraserSize'
const LS_ERASER_MODE = 'tm.eraserMode'
const LS_DOODLE = 'tm.doodle'
const LS_LABELS = 'tm.labels'
const LS_EXTRACTS = 'tm.extracts'
const LS_TASK_NAMES = 'tm.taskNames'
const LS_MENU = 'tm.menu'

type Tab = 'map' | 'search'

function randomName() {
  return `PMC-${Math.floor(1000 + Math.random() * 9000)}`
}

/** 表示は言語で変わるので、ここは辞書のキーだけを持つ（→ src/i18n.tsx） */
const TOOLS: { id: Tool; label: MsgKey; title: MsgKey }[] = [
  { id: 'hand', label: 'tool.hand.label', title: 'tool.hand.title' },
  { id: 'pin', label: 'tool.pin.label', title: 'tool.pin.title' },
  { id: 'pen', label: 'tool.pen.label', title: 'tool.pen.title' },
  { id: 'eraser', label: 'tool.eraser.label', title: 'tool.eraser.title' },
]

/** 3段階の見出し（細/中/太・小/中/大）。並びは PEN_WIDTHS / ERASER_SIZES と同じ */
const PEN_STEP_KEYS: MsgKey[] = ['app.size.thin', 'app.size.medium', 'app.size.thick']
const ERASER_STEP_KEYS: MsgKey[] = ['app.size.small', 'app.size.medium', 'app.size.large']

/**
 * 3段階の設定を localStorage から拾う。未設定・壊れていたら真ん中（中）に戻す。
 * 文字列のまま突き合わせるのは、未設定の null を Number() に通すと 0（＝細）に
 * 化けて、既定が「中」にならないため。
 */
function storedStep(key: string) {
  const raw = localStorage.getItem(key)
  return raw === '0' || raw === '1' || raw === '2' ? Number(raw) : 1
}

function storedWidth() {
  const n = Number(localStorage.getItem(LS_SIDE_W))
  return Number.isFinite(n) && n > 0 ? Math.max(SIDE_MIN, Math.min(SIDE_MAX, n)) : SIDE_DEFAULT
}

/** 地図を出せるマップ。SVG と座標変換の設定が揃っている10種だけ */
const MAP_LIST = TARKOV_MAP_LIST

/** そのマップで新しく置くピンの高さ。選んでいるフロアの真ん中を採る */
function heightOf(map: TarkovMapDef | null, floor: string | null): number {
  if (!map) return 0
  const layer = map.layers?.find((l) => l.svgLayer === floor)
  const range = layer?.height ?? map.heightRange
  return range ? (range[0] + range[1]) / 2 : 0
}

export default function App({ roomId }: { roomId: string }) {
  const { t, lang } = useI18n()
  const [mapId, setMapId] = useState<string>(() => {
    const saved = localStorage.getItem(LS_MAP)
    return saved && TARKOV_MAPS[saved] ? saved : (MAP_LIST[0]?.normalizedName ?? '')
  })
  /** 地上に重ねて出すフロア。null なら maps.json の既定どおり */
  const [floor, setFloor] = useState<string | null>(null)
  const [showAbout, setShowAbout] = useState(false)
  const [userName, setUserName] = useState(() => localStorage.getItem(LS_USER) ?? randomName())
  const [color, setColor] = useState(() => localStorage.getItem(LS_COLOR) ?? PIN_COLORS[0])
  const [tool, setTool] = useState<Tool>(
    () => (TOOLS.find((t) => t.id === localStorage.getItem(LS_TOOL))?.id ?? 'pin') as Tool,
  )
  const [penColor, setPenColor] = useState(() => localStorage.getItem(LS_PEN_COLOR) ?? PIN_COLORS[0])
  const [penStep, setPenStep] = useState(() => storedStep(LS_PEN_W))
  const [eraserStep, setEraserStep] = useState(() => storedStep(LS_ERASER_SIZE))
  const [eraserMode, setEraserMode] = useState<EraserMode>(() =>
    localStorage.getItem(LS_ERASER_MODE) === 'area' ? 'area' : 'stroke',
  )
  const [showDoodle, setShowDoodle] = useState(() => localStorage.getItem(LS_DOODLE) !== 'off')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // タブ・パネルの状態はこのブラウザだけのもの。共有はしないので各自が別のタブを開ける
  const [tab, setTab] = useState<Tab>(() => (localStorage.getItem(LS_TAB) === 'search' ? 'search' : 'map'))
  const [sideOpen, setSideOpen] = useState(() => localStorage.getItem(LS_SIDE) !== 'off')
  const [sideWidth, setSideWidth] = useState(storedWidth)
  const [showLocations, setShowLocations] = useState(() => localStorage.getItem(LS_LOCS) !== 'off')
  const [showLabels, setShowLabels] = useState(() => localStorage.getItem(LS_LABELS) !== 'off')
  const [showExtracts, setShowExtracts] = useState(() => localStorage.getItem(LS_EXTRACTS) !== 'off')
  const [showTaskNames, setShowTaskNames] = useState(() => localStorage.getItem(LS_TASK_NAMES) !== 'off')
  /** メニュー（共有バー・ツールバー・タブ・下部バー）を畳んで、マップと登録タスクだけにする */
  const [menuHidden, setMenuHidden] = useState(() => localStorage.getItem(LS_MENU) === 'off')

  const {
    pins,
    strokes,
    users,
    board: rawBoard,
    state,
    error: roomError,
    upsertPin,
    deletePin,
    clearPins,
    setBoard: setRawBoard,
    beginStroke,
    extendStroke,
    endStroke,
    eraseStrokes,
    splitStroke,
    clearStrokes,
  } = useRealtime(roomId, mapId, userName)
  const {
    tasks,
    kappaIds,
    canonicalOf,
    traders,
    maps: taskMaps,
    // 脱出地点と日本語のマップ名。タスクと同じ経路で取る（ゲーム内テキストなので同梱しない）
    mapMeta,
    byId,
    available: tasksAvailable,
    error: tasksError,
    prepState,
    requestPrep,
  } = useTasks()

  /**
   * ボードの中の「中身が同じで ID だけ違うタスク」を正のIDへ寄せる（→ src/taskVariants.ts）。
   * 読むときも書くときも通すので、2人が別版を登録していても一覧・図形は1件になり、
   * 次に誰かがボードを触った時点で共有データ側も1件に直る。
   */
  const board = useMemo(() => canonicalizeBoard(rawBoard, canonicalOf), [rawBoard, canonicalOf])

  const setBoard = useCallback(
    (next: Board | ((prev: Board) => Board)) =>
      setRawBoard((prev: Board) =>
        canonicalizeBoard(
          typeof next === 'function' ? next(canonicalizeBoard(prev, canonicalOf)) : next,
          canonicalOf,
        ),
      ),
    [setRawBoard, canonicalOf],
  )

  useEffect(() => localStorage.setItem(LS_USER, userName), [userName])
  useEffect(() => localStorage.setItem(LS_COLOR, color), [color])
  useEffect(() => localStorage.setItem(LS_TAB, tab), [tab])
  useEffect(() => localStorage.setItem(LS_SIDE, sideOpen ? 'on' : 'off'), [sideOpen])
  useEffect(() => localStorage.setItem(LS_SIDE_W, String(sideWidth)), [sideWidth])
  useEffect(() => localStorage.setItem(LS_LOCS, showLocations ? 'on' : 'off'), [showLocations])
  useEffect(() => localStorage.setItem(LS_TOOL, tool), [tool])
  useEffect(() => localStorage.setItem(LS_PEN_COLOR, penColor), [penColor])
  useEffect(() => localStorage.setItem(LS_PEN_W, String(penStep)), [penStep])
  useEffect(() => localStorage.setItem(LS_ERASER_SIZE, String(eraserStep)), [eraserStep])
  useEffect(() => localStorage.setItem(LS_ERASER_MODE, eraserMode), [eraserMode])
  useEffect(() => localStorage.setItem(LS_DOODLE, showDoodle ? 'on' : 'off'), [showDoodle])
  useEffect(() => localStorage.setItem(LS_LABELS, showLabels ? 'on' : 'off'), [showLabels])
  useEffect(() => localStorage.setItem(LS_EXTRACTS, showExtracts ? 'on' : 'off'), [showExtracts])
  useEffect(() => localStorage.setItem(LS_TASK_NAMES, showTaskNames ? 'on' : 'off'), [showTaskNames])
  useEffect(() => localStorage.setItem(LS_MENU, menuHidden ? 'off' : 'on'), [menuHidden])
  useEffect(() => {
    if (mapId) localStorage.setItem(LS_MAP, mapId)
  }, [mapId])
  // フロアはマップごとの概念なので、切り替えたら既定に戻す
  useEffect(() => setFloor(null), [mapId])

  const activeMap = useMemo(() => TARKOV_MAPS[mapId] ?? null, [mapId])
  const activeMeta = useMemo(() => mapMeta[mapId] ?? null, [mapMeta, mapId])
  /** 選択中のフロア。未選択なら maps.json の既定 */
  const visibleLayers = useMemo(
    () => (!activeMap ? [] : floor ? [floor] : defaultVisibleLayers(activeMap)),
    [activeMap, floor],
  )
  /** マップ名は日本語があればそちら。無ければ英語の displayName */
  const mapName = useCallback(
    (m: TarkovMapDef) => mapMeta[m.normalizedName]?.name || m.displayName,
    [mapMeta],
  )
  /**
   * normalizedName → 画面に出す名前。
   * 地図を出せない the-lab などもタスクには出てくるので、
   * `TARKOV_MAPS` に無いマップも `mapMeta` 側から名前を引けるようにしておく。
   */
  const mapLabels = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {}
    for (const m of MAP_LIST) out[m.normalizedName] = m.displayName
    for (const [key, meta] of Object.entries(mapMeta)) {
      if (meta.name) out[key] = meta.name
      for (const alias of meta.aliases) out[alias] = meta.name || key
    }
    return out
  }, [mapMeta])
  const selected = useMemo(() => pins.find((p) => p.id === selectedId) ?? null, [pins, selectedId])

  const handleAdd = useCallback(
    (pos: GamePos) => {
      if (!mapId) return
      const now = Date.now()
      const pin: Pin = {
        id: crypto.randomUUID(),
        mapId,
        pos,
        label: '',
        note: '',
        color,
        author: userName,
        createdAt: now,
        updatedAt: now,
      }
      upsertPin(pin)
      setSelectedId(pin.id)
    },
    [mapId, color, userName, upsertPin],
  )

  const handleMove = useCallback(
    (id: string, pos: GamePos) => {
      const pin = pins.find((p) => p.id === id)
      if (pin) upsertPin({ ...pin, pos })
    },
    [pins, upsertPin],
  )

  const patchSelected = useCallback(
    (patch: Partial<Pin>) => {
      if (!selected) return
      upsertPin({ ...selected, ...patch })
    },
    [selected, upsertPin],
  )

  /* ---- 落書き ---- */

  const penWidth = PEN_WIDTHS[penStep]
  const eraserSize = ERASER_SIZES[eraserStep]

  /** 自分が引いた線の id。Ctrl+Z はここからしか取り消さない（他人の線は触らない） */
  const myStrokesRef = useRef<string[]>([])
  useEffect(() => {
    myStrokesRef.current = []
  }, [mapId])

  const handleDrawBegin = useCallback(
    (id: string, pts: number[]) => {
      if (!mapId) return
      beginStroke({ id, mapId, pts, color: penColor, width: penWidth, author: userName, createdAt: Date.now() })
      const mine = myStrokesRef.current
      mine.push(id)
      // 取り消し履歴は直近ぶんだけあれば足りる
      if (mine.length > 200) mine.shift()
    },
    [mapId, penColor, penWidth, userName, beginStroke],
  )

  const handleErase = useCallback(
    (ids: string[]) => {
      const gone = new Set(ids)
      myStrokesRef.current = myStrokesRef.current.filter((id) => !gone.has(id))
      eraseStrokes(ids)
    },
    [eraseStrokes],
  )

  const handleSplit = useCallback(
    (id: string, parts: StrokePart[]) => {
      // 分割後は別の線になる。自分の履歴からは元の id を外し、断片を積み直す
      const mine = myStrokesRef.current.filter((x) => x !== id)
      if (myStrokesRef.current.length !== mine.length) mine.push(...parts.map((p) => p.id))
      myStrokesRef.current = mine
      splitStroke(id, parts)
    },
    [splitStroke],
  )

  /** ペン / 消しゴムを選んだら落書きの表示も戻す（描いても見えないのを防ぐ） */
  const chooseTool = useCallback((next: Tool) => {
    setTool(next)
    if (next === 'pen' || next === 'eraser') setShowDoodle(true)
  }, [])

  /** 自分が最後に引いた線を1本取り消す。共有する履歴は持たないので Redo は無い */
  const undoStroke = useCallback(() => {
    const mine = myStrokesRef.current
    const live = new Set(strokes.map((s) => s.id))
    // 既に誰かに消された線は飛ばす
    while (mine.length && !live.has(mine[mine.length - 1])) mine.pop()
    const id = mine.pop()
    if (id) eraseStrokes([id])
  }, [strokes, eraseStrokes])

  /* ---- タスクボード操作 ---- */

  const registerTask = useCallback(
    (slotId: string, taskId: string) => {
      setBoard((prev: Board) => ({
        ...prev,
        slots: prev.slots.map((s) =>
          s.id === slotId && !s.taskIds.includes(taskId) ? { ...s, taskIds: [...s.taskIds, taskId] } : s,
        ),
        // 一覧は重複を持たない。既にあれば順番はそのまま。
        order: prev.order.includes(taskId) ? prev.order : [...prev.order, taskId],
      }))
    },
    [setBoard],
  )

  const unregisterTask = useCallback(
    (slotId: string, taskId: string) => {
      setBoard((prev: Board) => {
        const slots = prev.slots.map((s) =>
          s.id === slotId ? { ...s, taskIds: s.taskIds.filter((t) => t !== taskId) } : s,
        )
        // 誰も登録していなくなったら一覧からも消す
        const stillUsed = slots.some((s) => s.taskIds.includes(taskId))
        if (stillUsed) return { ...prev, slots }
        return {
          ...prev,
          slots,
          order: prev.order.filter((t) => t !== taskId),
          hidden: prev.hidden.filter((t) => t !== taskId),
        }
      })
    },
    [setBoard],
  )

  const removeTaskEverywhere = useCallback(
    (taskId: string) => {
      setBoard((prev: Board) => ({
        ...prev,
        slots: prev.slots.map((s) => ({ ...s, taskIds: s.taskIds.filter((t) => t !== taskId) })),
        order: prev.order.filter((t) => t !== taskId),
        hidden: prev.hidden.filter((t) => t !== taskId),
      }))
    },
    [setBoard],
  )

  /**
   * 表示中のマップに関係するタスクか。
   * マップが1つも紐づかないタスク（渡すだけ・撃つだけ等）はどのマップでも出す。
   *
   * 旧方式にあった「マップ名の表記ゆれを吸収する」処理は要らなくなった。
   * tarkov.dev のデータは normalizedName で来るので、そのまま突き合わせられる。
   */
  const isVisibleTask = useCallback(
    (taskId: string) => {
      const task = byId.get(taskId)
      if (!task) return false
      if (task.maps.length === 0) return true
      if (!activeMap) return true
      const names = [activeMap.normalizedName, ...(activeMap.altNames ?? [])]
      return task.maps.some((m) => names.includes(m))
    },
    [byId, activeMap],
  )

  /** マップタブの一覧に出す並び。一覧とマップ上の図形の番号は、この並びの 1 始まり。 */
  const visibleOrder = useMemo(() => board.order.filter(isVisibleTask), [board.order, isVisibleTask])

  /**
   * 見えている並びだけを差し替える。`order` 内の位置は動かさないので、
   * 他のマップのタスクとの相対位置は変わらない。
   *
   *   order = [w1, c1, w2, c2] で CUSTOMS を [c2, c1] にすると → [w1, c2, w2, c1]
   */
  const applyVisibleOrder = useCallback(
    (next: string[]) => {
      setBoard((prev: Board) => {
        const slots: number[] = []
        prev.order.forEach((id, i) => {
          if (isVisibleTask(id)) slots.push(i)
        })
        // 送信の行き違いで並びがずれていたら触らない（次の board で作り直される）
        if (slots.length !== next.length) return prev
        const order = prev.order.slice()
        slots.forEach((pos, k) => {
          order[pos] = next[k]
        })
        return { ...prev, order }
      })
    },
    [setBoard, isVisibleTask],
  )

  const moveTask = useCallback(
    (taskId: string, dir: -1 | 1) => {
      const i = visibleOrder.indexOf(taskId)
      const j = i + dir
      if (i < 0 || j < 0 || j >= visibleOrder.length) return
      const next = visibleOrder.slice()
      ;[next[i], next[j]] = [next[j], next[i]]
      applyVisibleOrder(next)
    },
    [visibleOrder, applyVisibleOrder],
  )

  /** つかんだ行を、落とした行の位置へ入れる */
  const dropTask = useCallback(
    (taskId: string, targetId: string) => {
      const from = visibleOrder.indexOf(taskId)
      const to = visibleOrder.indexOf(targetId)
      if (from < 0 || to < 0 || from === to) return
      const next = visibleOrder.slice()
      next.splice(from, 1)
      next.splice(to, 0, taskId)
      applyVisibleOrder(next)
    },
    [visibleOrder, applyVisibleOrder],
  )

  /**
   * 表示中のマップの一覧を**タスク名の昇順**に並べ替える（A→Z）。
   *
   * 番号は一覧の並びそのものなので、見た目だけ並べ替えると人によって番号が食い違う。
   * `order` を書き換えて**全員に配る**（→ doc/task-board/master.md#並び順）。
   * 他のマップのタスクの位置は動かさない（並び替えの射影は `applyVisibleOrder` に任せる）。
   */
  const sortTasksByName = useCallback(() => {
    const next = visibleOrder
      .slice()
      .sort((a, b) => (byId.get(a)?.name ?? '').localeCompare(byId.get(b)?.name ?? '', lang))
    applyVisibleOrder(next)
  }, [visibleOrder, byId, applyVisibleOrder, lang])

  const renameSlot = useCallback(
    (slotId: string, name: string) => {
      setBoard((prev: Board) => ({
        ...prev,
        slots: prev.slots.map((s) => (s.id === slotId ? { ...s, name } : s)),
      }))
    },
    [setBoard],
  )

  const changeSlotCount = useCallback(
    (n: number) => {
      setBoard((prev: Board) => {
        const ids = ['A', 'B', 'C', 'D', 'E'].slice(0, n)
        const slots = ids.map((id) => prev.slots.find((s) => s.id === id) ?? { id, name: '', taskIds: [] })
        // 減らしたスロットに居たタスクは一覧からも外す
        const kept = new Set(slots.flatMap((s) => s.taskIds))
        return {
          slotCount: n,
          slots,
          order: prev.order.filter((t) => kept.has(t)),
          hidden: prev.hidden.filter((t) => kept.has(t)),
        }
      })
    },
    [setBoard],
  )

  /* ---- 非表示マーク（→ doc/task-board/master.md の「非表示マーク」） ---- */

  const hiddenSet = useMemo(() => new Set(board.hidden), [board.hidden])

  const toggleHidden = useCallback(
    (taskId: string) => {
      setBoard((prev: Board) => ({
        ...prev,
        hidden: prev.hidden.includes(taskId)
          ? prev.hidden.filter((t) => t !== taskId)
          : [...prev.hidden, taskId],
      }))
    },
    [setBoard],
  )

  /**
   * 表示中のマップに図形を持つタスクID。非表示ボタンはこれに入るタスクにだけ出す。
   * 図形が無いタスク（zone を持たない339件、一覧に「場所なし」と出るもの）に出しても
   * 押した見た目が変わらず、効かないボタンになるため。
   * zone はマップごとに付くので、マップを切り替えるとボタンの出方も変わる。
   */
  const markableTasks = useMemo(() => {
    if (!activeMap) return new Set<string>()
    const names = [activeMap.normalizedName, ...(activeMap.altNames ?? [])]
    const out = new Set<string>()
    for (const taskId of visibleOrder) {
      const task = byId.get(taskId)
      if (!task) continue
      if (task.objectives.some((obj) => obj.zones.some((z) => names.includes(z.map)))) out.add(taskId)
    }
    return out
  }, [activeMap, visibleOrder, byId])

  /**
   * 登録タスクの場所のうち、今表示しているマップのものだけを取り出す。
   * 番号はこのマップの一覧の通し番号（1始まり）で、同じタスクの複数箇所には同じ番号が付く。
   * 一覧に出ていないタスク（別マップのタスク）の場所は、番号を振れないので描かない。
   *
   * ポップアップに出す目標は、その zone を持つ objective のものだけに絞る
   * （タスク全体の目標を全部並べると、どれがその場所の話か分からなくなる）。
   */
  const locationMarks = useMemo<LocationMark[]>(() => {
    if (!showLocations || !activeMap) return []
    const names = [activeMap.normalizedName, ...(activeMap.altNames ?? [])]
    const [bx0, bz0] = activeMap.bounds[0]
    const [bx1, bz1] = activeMap.bounds[1]
    const inBounds = (p: GamePos) =>
      p.x >= Math.min(bx0, bx1) &&
      p.x <= Math.max(bx0, bx1) &&
      p.z >= Math.min(bz0, bz1) &&
      p.z <= Math.max(bz0, bz1)

    const marks = new Map<string, LocationMark>()
    visibleOrder.forEach((taskId, i) => {
      const task = byId.get(taskId)
      if (!task) return
      // 非表示のタスクは図形を描かないが、番号 i は消費したままにする。
      // 詰めると、マップに残っている他の図形の番号がずれてしまう
      if (hiddenSet.has(taskId)) return
      task.objectives.forEach((obj) => {
        obj.zones.forEach((zone) => {
          if (!names.includes(zone.map)) return

          // 同じ場所を複数の目標が指していることがある（Fishing Gear は3つ）。
          // そのまま描くと同じ図形と番号が重なるので、1つにまとめて目標だけ足す
          const key = `${taskId}:${zone.position.x.toFixed(1)}:${zone.position.z.toFixed(1)}`
          // 任意の目標は文の後ろに「（任意）」を付ける。ポップアップでも見分けが付くように
          const text = obj.optional ? `${objectiveText(obj)}（${t('tasklist.optional')}）` : objectiveText(obj)
          const existing = marks.get(key)
          if (existing) {
            if (text && !existing.objectives.includes(text)) existing.objectives.push(text)
            // 同じ場所を必須の目標も指しているなら、その場所は必須として描く
            existing.optional = existing.optional && obj.optional
            return
          }

          // まれに outline がマップの外を指しているデータがある
          // （Woods の Ice Cream Cones。459 zone 中1件）。position は全件が範囲内なので、
          // outline が丸ごと外に出ていたら信用せず、中心だけ描く
          const outline = zone.outline.some(inBounds) ? zone.outline : []

          marks.set(key, {
            id: key,
            taskId,
            no: i + 1,
            taskName: task.name,
            trader: task.trader,
            objectives: text ? [text] : [],
            optional: obj.optional,
            kappa: kappaIds.has(task.id),
            position: zone.position,
            outline,
            top: zone.top,
            bottom: zone.bottom,
          })
        })
      })
    })
    return [...marks.values()]
  }, [showLocations, activeMap, visibleOrder, byId, hiddenSet, kappaIds, t])

  /**
   * いま見えているフロアに出ない場所の数。
   *
   * zone は高さを持っていて、別のフロアにあるものは地図に出ない
   * （Customs の `Shaking Up the Teller` は 2nd Floor）。黙って消えると
   * 「登録したのに何も出ない」と見えるので、件数だけ出して気づけるようにする。
   */
  const hiddenByFloor = useMemo(() => {
    if (!activeMap) return 0
    return locationMarks.filter(
      (loc) => !visibleOnLayers(activeMap, visibleLayers, loc.position, loc.top, loc.bottom),
    ).length
  }, [locationMarks, activeMap, visibleLayers])

  // 登録者の表示は、名前が入っていればその名前。空ならスロットID（A/B/…）で代用する
  const ownersOf = useCallback(
    (taskId: string) =>
      board.slots
        .filter((s) => s.taskIds.includes(taskId))
        .map((s) => ({ id: s.id, label: s.name.trim() || s.id })),
    [board.slots],
  )

  // Delete / Backspace で選択中のピンを消す（入力欄にフォーカスがある間は無効）
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (tab !== 'map') return
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault()
        undoStroke()
        return
      }
      if (e.key === 'Escape') setSelectedId(null)
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        e.preventDefault()
        deletePin(selectedId)
        setSelectedId(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedId, deletePin, tab, undoStroke])

  const stateLabel = t(`app.state.${state}`)

  return (
    // マップタブのあいだは viewport に高さを固定する。min-height だけだと登録が増えたときに
    // 一覧の高さでページごと伸び、マップまで縦に間延びする（→ doc/layout/master.md）
    <div className={`app${tab === 'map' ? ' app--map' : ''}${menuHidden && tab === 'map' ? ' app--bare' : ''}`}>
      <ShareBar roomId={roomId} state={state} error={roomError} />
      {/* ツールバーは常に2段。中身が増減しても段数を変えない（→ doc/layout/master.md）。
          1段でまとめて折り返すと、タブやウィンドウ幅で段数が変わり、
          下のタブ行が上下に動いてクリック位置がずれる。 */}
      <header className="toolbar">
        <div className="toolbar__row">
          <span className="brand">Tarkov Map Pins</span>

          <select
            className="control"
            value={mapId}
            onChange={(e) => {
              setMapId(e.target.value)
              setSelectedId(null)
            }}
            aria-label={t('app.map')}
          >
            {MAP_LIST.map((m) => (
              <option key={m.normalizedName} value={m.normalizedName}>
                {mapName(m)}
              </option>
            ))}
          </select>

          {/* フロア。SVG には全階が重なって入っているので、出す階を選ぶ */}
          {tab === 'map' && !!activeMap?.layers?.length && (
            <select
              className="control"
              value={floor ?? ''}
              onChange={(e) => setFloor(e.target.value || null)}
              aria-label={t('app.floor')}
              title={t('app.floor.title')}
            >
              <option value="">{t('app.floor.ground')}</option>
              {activeMap.layers.map((l) => (
                <option key={l.svgLayer} value={l.svgLayer}>
                  {l.name}
                </option>
              ))}
            </select>
          )}

          <div className="tools" role="group" aria-label={t('app.tools')}>
            {TOOLS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`control tool${tool === item.id ? ' tool--on' : ''}`}
                onClick={() => chooseTool(item.id)}
                title={t(item.title)}
                aria-pressed={tool === item.id}
              >
                {t(item.label)}
              </button>
            ))}
          </div>

          {/* 色見本は1列のまま、選んでいるツールで意味が変わる（ピンの色 / 線の色） */}
          {tool !== 'eraser' && (
            <div
              className="swatches"
              role="group"
              aria-label={tool === 'pen' ? t('app.penColor') : t('app.pinColor')}
            >
              {PIN_COLORS.map((c) => {
                const on = tool === 'pen' ? c === penColor : c === color
                return (
                  <button
                    key={c}
                    type="button"
                    className={`swatch${on ? ' swatch--on' : ''}`}
                    style={{ background: c }}
                    onClick={() => (tool === 'pen' ? setPenColor(c) : setColor(c))}
                    aria-label={t('app.color', { c })}
                  />
                )
              })}
            </div>
          )}

          {tool === 'pen' && (
            <div className="steps" role="group" aria-label={t('app.penWidth')}>
              {PEN_WIDTHS.map((w, i) => (
                <button
                  key={w}
                  type="button"
                  className={`step${penStep === i ? ' step--on' : ''}`}
                  onClick={() => setPenStep(i)}
                  title={t('app.penWidth.step', { size: t(PEN_STEP_KEYS[i]) })}
                  aria-label={t('app.penWidth.step', { size: t(PEN_STEP_KEYS[i]) })}
                >
                  <span className="step__dot" style={{ width: 4 + i * 5, height: 4 + i * 5 }} />
                </button>
              ))}
            </div>
          )}

          {tool === 'eraser' && (
            <>
              <div className="steps" role="group" aria-label={t('app.eraserSize')}>
                {ERASER_SIZES.map((sz, i) => (
                  <button
                    key={sz}
                    type="button"
                    className={`step${eraserStep === i ? ' step--on' : ''}`}
                    onClick={() => setEraserStep(i)}
                    title={t('app.eraserSize.step', { size: t(ERASER_STEP_KEYS[i]) })}
                    aria-label={t('app.eraserSize.step', { size: t(ERASER_STEP_KEYS[i]) })}
                  >
                    <span className="step__ring" style={{ width: 6 + i * 5, height: 6 + i * 5 }} />
                  </button>
                ))}
              </div>

              <div className="steps" role="group" aria-label={t('app.eraserMode')}>
                <button
                  type="button"
                  className={`control seg${eraserMode === 'stroke' ? ' seg--on' : ''}`}
                  onClick={() => setEraserMode('stroke')}
                  title={t('app.eraser.stroke.title')}
                >
                  {t('app.eraser.stroke')}
                </button>
                <button
                  type="button"
                  className={`control seg${eraserMode === 'area' ? ' seg--on' : ''}`}
                  onClick={() => setEraserMode('area')}
                  title={t('app.eraser.area.title')}
                >
                  {t('app.eraser.area')}
                </button>
              </div>

              {/* 部分消しは1本の線を分割する。長くなぞるほど破片が増えるので、先に断っておく
                  → doc/doodle/master.md */}
              {eraserMode === 'area' && (
                <span
                  className="hint hint--warn"
                  role="status"
                  title={t('app.eraser.warn.title')}
                >
                  {t('app.eraser.warn')}
                </span>
              )}
            </>
          )}
        </div>

        <div className="toolbar__row">
          {tab === 'map' && (
            <>
              <button
                type="button"
                className={`control toggle${showDoodle ? ' toggle--on' : ''}`}
                onClick={() => setShowDoodle((v) => !v)}
                title={t('app.toggle.doodle.title')}
              >
                {t('app.toggle.doodle')} {showDoodle ? t('app.on') : t('app.off')}
              </button>

              {tasksAvailable && (
                <>
                  <button
                    type="button"
                    className={`control toggle${sideOpen ? ' toggle--on' : ''}`}
                    onClick={() => setSideOpen((v) => !v)}
                    title={t('app.toggle.list.title')}
                  >
                    {t('app.toggle.list')} {sideOpen ? t('app.on') : t('app.off')}
                  </button>

                  <button
                    type="button"
                    className={`control toggle${showLocations ? ' toggle--on' : ''}`}
                    onClick={() => setShowLocations((v) => !v)}
                    title={t('app.toggle.locations.title')}
                  >
                    {t('app.toggle.locations')} {showLocations ? t('app.on') : t('app.off')}
                  </button>

                  {/* 番号だけでは何のタスクか分からないので、名前も出せるようにする */}
                  <button
                    type="button"
                    className={`control toggle${showTaskNames ? ' toggle--on' : ''}`}
                    onClick={() => setShowTaskNames((v) => !v)}
                    title={t('app.toggle.taskNames.title')}
                    disabled={!showLocations}
                  >
                    {t('app.toggle.taskNames')} {showTaskNames ? t('app.on') : t('app.off')}
                  </button>
                </>
              )}

              {/* SVG マップは意図的に文字を持たない。地名も脱出地点もアプリ側で描いている */}
              <button
                type="button"
                className={`control toggle${showLabels ? ' toggle--on' : ''}`}
                onClick={() => setShowLabels((v) => !v)}
                title={t('app.toggle.labels.title')}
              >
                {t('app.toggle.labels')} {showLabels ? t('app.on') : t('app.off')}
              </button>

              <button
                type="button"
                className={`control toggle${showExtracts ? ' toggle--on' : ''}`}
                onClick={() => setShowExtracts((v) => !v)}
                title={t('app.toggle.extracts.title')}
              >
                {t('app.toggle.extracts')} {showExtracts ? t('app.on') : t('app.off')}
              </button>
            </>
          )}

          <div className="spacer" />

          <LangToggle />

          <button
            type="button"
            className="control toggle"
            onClick={() => setShowAbout(true)}
            title={t('app.about.title')}
          >
            {t('app.about')}
          </button>

          <input
            className="control name"
            value={userName}
            onChange={(e) => setUserName(e.target.value.slice(0, 40))}
            placeholder={t('app.name')}
            aria-label={t('app.name')}
          />

          <span className={`status status--${state}`} title={stateLabel}>
            ● {stateLabel}
            {state === 'open' && users.length > 0 && ` / ${t('app.users', { n: users.length })}`}
          </span>
        </div>
      </header>

      <nav className="tabs" role="tablist" aria-label={t('app.tabs')}>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'map'}
          className={`tab${tab === 'map' ? ' tab--on' : ''}`}
          onClick={() => setTab('map')}
        >
          {t('app.tab.map')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'search'}
          className={`tab${tab === 'search' ? ' tab--on' : ''}`}
          onClick={() => setTab('search')}
        >
          {t('app.tab.search')}
        </button>
      </nav>

      {/* 両タブとも常にマウントしたまま出し入れする。
          マップの拡大位置と検索条件をタブの往復で失わないため。 */}
      <div className={`pane pane--map${tab === 'map' ? ' pane--on' : ''}`}>
        <main className="stage">
          {/* タスクデータが無くても、マップ・ピン・落書きは使える（→ doc/sharing/master.md#エラー処理） */}
          {tasksError && (
            <div className="notice notice--error">{t('app.tasksError', { msg: tasksError })}</div>
          )}

          {activeMap && (
            <MapView
              key={activeMap.normalizedName}
              map={activeMap}
              extracts={activeMeta?.extracts ?? []}
              visibleLayers={visibleLayers}
              pins={pins}
              locations={locationMarks}
              strokes={strokes}
              selectedId={selectedId}
              tool={tool}
              doodleVisible={showDoodle}
              labelsVisible={showLabels}
              extractsVisible={showExtracts}
              taskNamesVisible={showTaskNames}
              pinHeight={heightOf(activeMap, floor)}
              eraser={{ size: eraserSize, mode: eraserMode }}
              onSelect={setSelectedId}
              onAdd={handleAdd}
              onMove={handleMove}
              onDrawBegin={handleDrawBegin}
              onDrawPoints={extendStroke}
              onDrawEnd={endStroke}
              onErase={handleErase}
              onSplit={handleSplit}
            />
          )}

          {selected && (
            <aside className="editor">
              <div className="editor__head">
                <input
                  className="editor__title"
                  value={selected.label}
                  onChange={(e) => patchSelected({ label: e.target.value.slice(0, 120) })}
                  placeholder={t('app.pin.label.placeholder')}
                  autoFocus
                />
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => setSelectedId(null)}
                  aria-label={t('app.close')}
                >
                  ×
                </button>
              </div>

              <textarea
                className="editor__note"
                value={selected.note}
                onChange={(e) => patchSelected({ note: e.target.value.slice(0, 500) })}
                placeholder={t('app.pin.note.placeholder')}
                rows={3}
              />

              <div className="swatches">
                {PIN_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`swatch${c === selected.color ? ' swatch--on' : ''}`}
                    style={{ background: c }}
                    onClick={() => patchSelected({ color: c })}
                    aria-label={t('app.color', { c })}
                  />
                ))}
              </div>

              <div className="editor__foot">
                <span className="editor__meta">{selected.author}</span>
                <button
                  type="button"
                  className="danger"
                  onClick={() => {
                    deletePin(selected.id)
                    setSelectedId(null)
                  }}
                >
                  {t('app.delete')}
                </button>
              </div>
            </aside>
          )}

          {/* マップの右上。左から順に並べる（一覧を閉じているときだけ ⟨ 一覧 が出る） */}
          <div className="map-tools">
            <button
              type="button"
              className="map-tool"
              onClick={() => setMenuHidden((v) => !v)}
              aria-pressed={menuHidden}
              title={menuHidden ? t('app.menu.show.title') : t('app.menu.hide.title')}
            >
              {menuHidden ? t('app.menu.show') : t('app.menu.hide')}
            </button>

            {tasksAvailable && !sideOpen && (
              <button
                type="button"
                className="map-tool"
                onClick={() => setSideOpen(true)}
                title={t('app.side.open.title')}
              >
                {t('app.side.open')}
              </button>
            )}
          </div>

          {activeMap && (
            <div className="footbar">
              <span>{t('app.footbar.counts', { pins: pins.length, strokes: strokes.length })}</span>
              {hiddenByFloor > 0 && (
                <span className="footbar__hint" title={t('app.footbar.otherFloor.title')}>
                  {t('app.footbar.otherFloor', { n: hiddenByFloor })}
                </span>
              )}
              {users.length > 0 && (
                <span className="footbar__users">
                  {users.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      /* 自分と違うマップを見ている人は控えめに出す。押すとそのマップへ移る
                         （→ doc/realtime-pins/master.md#presence） */
                      className={`user${u.mapId === mapId ? ' user--here' : ''}`}
                      disabled={!u.mapId || u.mapId === mapId}
                      onClick={() => u.mapId && setMapId(u.mapId)}
                      title={
                        u.mapId
                          ? t('app.user.viewing', { map: mapLabels[u.mapId] ?? u.mapId })
                          : t('app.user.noMap')
                      }
                    >
                      {u.drawing && <span aria-label={t('app.user.drawing')}>✏️</span>}
                      {u.name}
                      {u.mapId && u.mapId !== mapId && <span className="user__map"> — {(mapLabels[u.mapId] ?? u.mapId)}</span>}
                    </button>
                  ))}
                </span>
              )}
              <button
                type="button"
                className="danger ghost"
                disabled={pins.length === 0}
                onClick={() => {
                  if (confirm(t('app.clearPins.confirm', { map: mapName(activeMap) }))) {
                    clearPins()
                    setSelectedId(null)
                  }
                }}
              >
                {t('app.clearPins')}
              </button>
              <button
                type="button"
                className="danger ghost"
                disabled={strokes.length === 0}
                onClick={() => {
                  if (confirm(t('app.clearDoodle.confirm', { map: mapName(activeMap) }))) {
                    clearStrokes()
                    myStrokesRef.current = []
                  }
                }}
              >
                {t('app.clearDoodle')}
              </button>
            </div>
          )}
        </main>

        {/* 登録タスクの一覧。マップと並べて常に見えるようにする */}
        {tasksAvailable && (
          <SidePanel
            open={sideOpen}
            width={sideWidth}
            onToggle={() => setSideOpen((v) => !v)}
            onResize={setSideWidth}
            // 一覧の上に貼り付ける持ち込みリスト。一覧をスクロールしても動かない
            // → doc/task-prep/master.md
            top={
              visibleOrder.length > 0 ? (
                <TaskPrepPanel
                  order={visibleOrder}
                  byId={byId}
                  hiddenIds={hiddenSet}
                  prepState={prepState}
                  onNeedPrep={requestPrep}
                />
              ) : null
            }
          >
            <TaskList
              order={visibleOrder}
              registeredCount={board.order.length}
              mapName={activeMap ? mapName(activeMap) : ''}
              mapLabels={mapLabels}
              byId={byId}
              kappaIds={kappaIds}
              ownersOf={ownersOf}
              hiddenIds={hiddenSet}
              markableIds={markableTasks}
              onToggleHidden={toggleHidden}
              onMove={moveTask}
              onDrop={dropTask}
              onSortByName={sortTasksByName}
              onRemove={removeTaskEverywhere}
            />
          </SidePanel>
        )}
      </div>

      {/* 人数ぶんの検索・登録欄。列が画面の縦幅いっぱいを使う */}
      <div className={`pane pane--search${tab === 'search' ? ' pane--on' : ''}`}>
        {!tasksAvailable && (
          <div className="board__notice">
            {tasksError ? (
              <>{t('board.notice.error', { msg: tasksError })}</>
            ) : (
              <>{t('board.notice.loading')}</>
            )}
          </div>
        )}

        {tasksAvailable && (
          <>
            <div className="board__bar">
              <label className="board__people">
                {t('board.people')}
                <select value={board.slotCount} onChange={(e) => changeSlotCount(Number(e.target.value))}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>
                      {t('board.people.n', { n })}
                    </option>
                  ))}
                </select>
              </label>
              <span className="board__hint">{t('board.hint', { n: tasks.length })}</span>
            </div>

            <div className="slots">
              {board.slots.map((slot) => (
                <SlotPanel
                  key={slot.id}
                  slot={slot}
                  tasks={tasks}
                  byId={byId}
                  kappaIds={kappaIds}
                  traders={traders}
                  maps={taskMaps}
                  mapLabels={mapLabels}
                  onRename={(name) => renameSlot(slot.id, name)}
                  onRegister={(taskId) => registerTask(slot.id, taskId)}
                  onUnregister={(taskId) => unregisterTask(slot.id, taskId)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {showAbout && <AboutDialog onClose={() => setShowAbout(false)} />}
    </div>
  )
}
